import React, { useEffect, useState } from "react";
import { Carousel, Table } from "react-bootstrap";
import Badge from "react-bootstrap/Badge";

import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import ClockWithHighlight from "./../../components/customComponents/ClockWithHighligth";
import mexicoGeoJson from "./../../utils/mexico_regions.geojson";
import { mesesCatalogo } from "./../../utils/catalogos";

import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
  CartesianGrid,
} from "recharts";

import { Box, Paper } from "@mui/material";

const segmentos = [
  {
    titulo: "Matutino",
    rango: ["07:00 AM", "12:00 PM"],
    color: "#d72e09",
    startAngle: 150,
    endAngle: 270,
  },
  {
    titulo: "Vespertino",
    rango: ["12:00 PM", "07:00 PM"],
    color: "#188afd",
    startAngle: -90,
    endAngle: 121,
  },
  {
    titulo: "Nocturno",
    rango: ["07:00 PM", "12:00 AM"],
    color: "#188afd",
    startAngle: 150,
    endAngle: 270,
  },
  {
    titulo: "Madrugada",
    rango: ["12:00 AM", "07:00 AM"],
    color: "#d72e09",
    startAngle: -90,
    endAngle: 121,
  },
];

const circleStyle = (color) => ({
  display: "inline-block",
  width: 12,
  height: 12,
  borderRadius: "50%",
  backgroundColor: color,
  marginRight: 6,
});

// Función para preparar los datos para la gráfica
const prepararDataGrafico = (data = []) => {
  return [
    {
      name: "Boletinada",
      ...data.reduce((acc, item) => {
        acc[item.tipo_unidad] = item.boletinados;
        return acc;
      }, {}),
    },
    {
      name: "Recuperado",
      ...data.reduce((acc, item) => {
        acc[item.tipo_unidad] = item.recuperados;
        return acc;
      }, {}),
    },
  ];
};

// Etiqueta personalizada con porcentaje sobre el gráfico
const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
}) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.55;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={12}
      fontWeight="bold"
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

export const MapaEventos = ({ data = [] }) => {
  const [datosParaMapa, setDatosParaMapa] = useState(data);
  const [tooltipContent, setTooltipContent] = useState("");
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (data && data.length > 0) {
      const conteosPorEntidad = data.reduce((acc, curr) => {
        acc[curr.entidad_id] = (acc[curr.entidad_id] || 0) + 1;
        return acc;
      }, {});

      const nuevosDatosParaMapa = Object.entries(conteosPorEntidad).map(
        ([entidad_id, conteo]) => ({
          id_estado: parseInt(entidad_id),
          conteo,
        })
      );

      setDatosParaMapa(nuevosDatosParaMapa);
    }
  }, [data]);

  const copyTableToClipboard = (mode = "entidad") => {
    if (!data || data.length === 0) return;

    let header = [];
    let rows = [];

    if (mode === "entidad") {
      const totalRobos = data.length;
      const conteosPorEntidad = data.reduce((acc, curr) => {
        acc[curr.entidad] = (acc[curr.entidad] || 0) + 1;
        return acc;
      }, {});

      const entidadesOrdenadas = Object.entries(conteosPorEntidad).sort(
        ([, a], [, b]) => b - a
      );

      header = ["Estado", "Total", "Porcentaje"];
      rows = entidadesOrdenadas.map(([entidad, conteo]) => {
        const porcentaje = ((conteo / totalRobos) * 100).toFixed(2);
        return [entidad, conteo, porcentaje].join("\t");
      });
    } else if (mode === "municipio") {
      const conteosPorEntidadMunicipio = data.reduce((acc, curr) => {
        const key = `${curr.entidad}-${curr.municipio}`;
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {});

      const conteosOrdenados = Object.entries(conteosPorEntidadMunicipio).sort(
        ([, a], [, b]) => b - a
      );

      header = ["Estado", "Municipio", "Total"];
      rows = conteosOrdenados.map(([key, conteo]) => {
        const [entidad, municipio] = key.split("-");
        return [entidad, municipio, conteo].join("\t");
      });
    }

    const text = [header.join("\t"), ...rows].join("\n");

    navigator.clipboard
      .writeText(text)
      .then(() => alert("Tabla copiada al portapapeles!"))
      .catch((err) => console.error("Error copiando tabla:", err));
  };

  const getColor = (conteo) => {
    if (conteo >= 5) return "red";
    if (conteo >= 2) return "#FFDB00";
    if (conteo >= 1) return "green";
    return "gray";
  };

  const handleMouseMove = (event, geo) => {
    const { name } = geo.properties;
    setTooltipContent(name);

    // usamos pageX/Y porque funcionan mejor con SVG
    setTooltipPosition({
      x: event.pageX + 15,
      y: event.pageY - 20,
    });
  };

  const handleMouseLeave = () => {
    setTooltipContent("");
  };

  return (
    <Carousel interval={null} fade>
      {/* Slide 1: Tabla */}
      <Carousel.Item>
        <div
          className="card border-light"
          style={{
            position: "relative",
            overflow: "hidden",
            borderRadius: "12px",
            boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
          }}
        >
          {/* Mapa centrado */}
          <div
            className="card-body d-flex justify-content-center"
            style={{
              position: "relative", // 👈 importante para el tooltip
              backgroundColor: "#F8FAFC",
              paddingBottom: "2rem",
            }}
          >
            <ComposableMap
              projection="geoMercator"
              width={950}
              height={300}
              projectionConfig={{
                scale: 850,
                center: [-102, 23],
              }}
            >
              <Geographies geography={mexicoGeoJson}>
                {({ geographies }) =>
                  geographies.map((geo) => {
                    const estadoId = geo.properties.estado_id;
                    const estadoData = datosParaMapa.find(
                      (d) => d.id_estado === estadoId
                    );
                    const conteo = estadoData ? estadoData.conteo : 0;
                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        onMouseMove={(event) => handleMouseMove(event, geo)}
                        onMouseLeave={handleMouseLeave}
                        fill={getColor(conteo)}
                        stroke="#FFF"
                        style={{
                          default: { outline: "none" },
                          hover: { outline: "none", opacity: 0.8 },
                        }}
                      />
                    );
                  })
                }
              </Geographies>
            </ComposableMap>

            {/* Tooltip */}
            {tooltipContent && (
              <div
                style={{
                  position: "absolute",
                  top: tooltipPosition.y - 100, // ajuste para que no tape el cursor
                  left: tooltipPosition.x - 300,
                  background: "rgba(0, 0, 0, 0.75)",
                  color: "white",
                  padding: "6px 10px",
                  borderRadius: "6px",
                  fontSize: "0.9rem",
                  pointerEvents: "none",
                  zIndex: 1000,
                  whiteSpace: "nowrap",
                }}
              >
                {tooltipContent}
              </div>
            )}
          </div>

          {/* Simbología */}
          <div
            className="card-footer text-body-secondary"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-evenly",
              flexWrap: "wrap",
              textAlign: "center",
              backgroundColor: "#FFFFFF",
              borderTop: "1px solid #E2E8F0",
              padding: "10px 0",
              fontSize: "0.9rem",
            }}
          >
            <div>
              <span style={circleStyle("red")}></span> 5 o más robos
            </div>
            <div>
              <span style={circleStyle("#FFDB00")}></span> De 2 a 4 robos
            </div>
            <div>
              <span style={circleStyle("green")}></span> 1 robo
            </div>
            <div>
              <span style={circleStyle("gray")}></span> Sin robos
            </div>
          </div>
        </div>
      </Carousel.Item>
      <Carousel.Item>
        <div
          onClick={() => copyTableToClipboard("entidad")}
          style={{
            maxHeight: "250px",
            overflowY: "scroll",
            overflowX: "hidden",
            borderRadius: "8px",
            border: "1px solid #e0e0e0",
            scrollbarWidth: "none", // 👈 oculta scrollbar en Firefox
            msOverflowStyle: "none", // 👈 IE/Edge
          }}
          className="scroll-container cursor"
        >
          <table
            className="table table-hover align-middle"
            style={{
              marginBottom: 0,
              fontSize: "0.9rem",
              width: "100%",
              minWidth: "400px",
            }}
          >
            <thead
              style={{
                position: "sticky",
                top: 0,
                background: "#f8f9fa",
                zIndex: 2,
              }}
            >
              <tr>
                <th className="text-center">Semáforo</th>
                <th>Estado</th>
                <th>Robos</th>
                <th>Porcentaje</th>
              </tr>
            </thead>
            <tbody>
              {data &&
                (() => {
                  const totalRobos = data.length;
                  const conteosPorEntidad = data.reduce((acc, curr) => {
                    acc[curr.entidad] = (acc[curr.entidad] || 0) + 1;
                    return acc;
                  }, {});

                  const entidadesOrdenadas = Object.entries(
                    conteosPorEntidad
                  ).sort(([, a], [, b]) => b - a);

                  return entidadesOrdenadas.map(([entidad, conteo]) => {
                    const porcentaje = ((conteo / totalRobos) * 100).toFixed(2);
                    const color =
                      conteo >= 5 ? "red" : conteo >= 2 ? "#FFDB00" : "green";

                    return (
                      <tr key={entidad}>
                        <td className="text-center">
                          <span
                            style={{
                              display: "inline-block",
                              width: 14,
                              height: 14,
                              borderRadius: "50%",
                              backgroundColor: color,
                            }}
                          ></span>
                        </td>
                        <td className="text-center">{entidad}</td>
                        <td className="text-center">{conteo}</td>
                        <td className="text-center">{porcentaje}%</td>
                      </tr>
                    );
                  });
                })()}
            </tbody>
            <tfoot>
              <tr className="text-success fw-bold">
                <td></td>
                <td className="text-center">Total</td>
                <td className="text-center">{data ? data.length : 0}</td>
                <td className="text-center">100%</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Carousel.Item>
      <Carousel.Item>
        <div
          onClick={() => copyTableToClipboard("municipio")}
          style={{
            maxHeight: "250px",
            overflowY: "scroll",
            overflowX: "hidden",
            borderRadius: "8px",
            border: "1px solid #e0e0e0",
            scrollbarWidth: "none", // 👈 oculta scrollbar en Firefox
            msOverflowStyle: "none", // 👈 IE/Edge
          }}
          className="scroll-container cursor"
        >
          <table
            className="table table-hover align-middle"
            style={{
              marginBottom: 0,
              fontSize: "0.9rem",
              width: "100%",
              minWidth: "400px",
            }}
          >
            <thead
              style={{
                position: "sticky",
                top: 0,
                background: "#f8f9fa",
                zIndex: 2,
              }}
            >
              <tr>
                <th>Entidad</th>
                <th>Municipio</th>
                <th>Conteo</th>
              </tr>
            </thead>
            <tbody>
              {data &&
                (() => {
                  // Calculamos el total de robos después del filtro
                  // const totalRobos = datosFiltrados.length;

                  // Contamos los robos por municipio y entidad
                  const conteosPorEntidadMunicipio = data.reduce(
                    (acc, curr) => {
                      const key = `${curr.entidad}-${curr.municipio}`; // Crear una clave única para entidad y municipio
                      acc[key] = (acc[key] || 0) + 1;
                      return acc;
                    },
                    {}
                  );

                  // Convertimos el objeto a un array y lo ordenamos por el número de robos (de mayor a menor)
                  const conteosOrdenados = Object.entries(
                    conteosPorEntidadMunicipio
                  ).sort(([, a], [, b]) => b - a);

                  // Mapeamos el conteo para mostrar la entidad, municipio y robos
                  return conteosOrdenados.map(([key, conteo], index) => {
                    const [entidad, municipio] = key.split("-"); // Descomponemos la clave en entidad y municipio

                    return (
                      <tr key={index}>
                        <td className="text-center">{entidad}</td>
                        <td className="text-center">{municipio}</td>
                        <td className="text-center">{conteo}</td>
                      </tr>
                    );
                  });
                })()}
            </tbody>
            <tfoot>
              <tr className="text-green">
                <td>Total</td>
                <td></td>
                <td>{data && data.length}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Carousel.Item>
    </Carousel>
  );
};

export const HorariosRobo = ({ data = [] }) => {
  // Filtramos solo los rangos con conteo > 0
  const dataConDatos = data.filter((item) => item.conteo > 0);

  // Si no hay datos, mostramos mensaje
  if (!dataConDatos || dataConDatos.length === 0) {
    return (
      <div className="text-center text-muted py-5">
        Sin datos disponibles para los horarios de robo
      </div>
    );
  }

  // Filtramos también los segmentos para los relojes
  const segmentosConDatos = segmentos.filter(
    (seg, index) => dataConDatos[index]?.conteo > 0
  );

  return (
    <Carousel interval={null} fade>
      {/* --- Slide 1: Relojes dinámicos --- */}
    <Carousel.Item>
  <div className="card border-light">
    <div
      className="card-body d-flex justify-content-between align-items-start"
      style={{
        gap: "0.5rem",
        flexWrap: "nowrap",
        padding: "1rem",
      }}
    >
      {segmentosConDatos.map((seg, index) => {
        const item = dataConDatos[index];

        return (
          <div
            key={index}
            className="d-flex flex-column align-items-center"
            style={{
              flex: "1 1 0", // 🔥 distribuye proporcionalmente el espacio
              minWidth: 0, // evita que se rompa el layout
              textAlign: "center",
            }}
          >
            <h6 style={{ fontSize: "0.9rem" }}>{seg.titulo}</h6>

            <div
              style={{
                width: "100%",
                aspectRatio: "1", // mantiene proporción cuadrada
                maxWidth: 120, // 🔥 tamaño máximo de cada reloj
              }}
            >
              <ClockWithHighlight
                startAngle={seg.startAngle}
                endAngle={seg.endAngle}
                color={seg.color}
              />
            </div>

            <div className="mt-2">
              <Badge
                bg="danger"
                style={{ fontSize: "0.8rem", padding: "4px 8px" }}
              >
                {item.porcentaje ?? 0}%
              </Badge>
            </div>
          </div>
        );
      })}
    </div>
  </div>
</Carousel.Item>



      {/* --- Slide 2: Tabla --- */}
      <Carousel.Item>
        <Table hover bordered className="table-sm table-responsive">
          <thead className="table-light">
            <tr>
              <th scope="col">Rango de horarios</th>
              <th scope="col">Total</th>
              <th scope="col">% de eventos</th>
            </tr>
          </thead>
          <tbody>
            {dataConDatos.map(({ horario, conteo, porcentaje }, index) => (
              <tr key={index}>
                <td className="text-center">{horario}</td>
                <td className="text-center">{conteo}</td>
                <td className="text-center">{porcentaje} %</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Carousel.Item>
    </Carousel>
  );
};

export const GraficaEstatusEvento = ({ data = [] }) => {
  // Filtra los tipos de vehículo que no tienen datos (robos, recuperados y boletinados = 0)
  const dataFiltrada = data.filter(
    (d) =>
      (d.robos || 0) > 0 || (d.recuperados || 0) > 0 || (d.boletinados || 0) > 0
  );

  // Si no hay datos, no mostramos nada
  if (dataFiltrada.length === 0) {
    return (
      <div className="text-center text-muted p-4">
        No hay datos disponibles para mostrar.
      </div>
    );
  }

  const chartData = prepararDataGrafico(dataFiltrada);

  // Colores por tipo de vehículo
  const colors = {
    Motos: "#22598F",
    Camiones: "#007BFF",
    Autos: "#91C9FF",
  };

  const tiposVehiculo = dataFiltrada.map((d) => d.tipo_unidad);

  return (
    <Carousel interval={null} fade>
      {/* --- Slide 1: Gráfica --- */}
      <Carousel.Item>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          >
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />

            {tiposVehiculo.map((tipo) => (
              <Bar
                key={tipo}
                dataKey={tipo}
                stackId="a"
                fill={colors[tipo] || "#8884d8"}
                barSize={40}
                radius={[6, 6, 0, 0]}
              >
                <LabelList
                  dataKey={tipo}
                  position="top"
                  formatter={(value) => {
                    const total = chartData
                      .map((d) => Object.values(d).slice(1))
                      .flat()
                      .reduce((a, b) => a + b, 0);
                    const porcentaje = total
                      ? ((value / total) * 100).toFixed(0)
                      : 0;
                    return `${porcentaje}%`;
                  }}
                  style={{ fill: "#333", fontSize: 12, fontWeight: "bold" }}
                />
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Carousel.Item>

      {/* --- Slide 2: Tabla --- */}
      <Carousel.Item>
        <div
          style={{
            maxHeight: "250px",
            overflowY: "scroll",
            overflowX: "hidden",
            borderRadius: "8px",
            border: "1px solid #e0e0e0",
            scrollbarWidth: "none", // Firefox
            msOverflowStyle: "none", // IE/Edge
          }}
          className="scroll-container"
        >
          <table
            className="table table-hover align-middle"
            style={{
              marginBottom: 0,
              fontSize: "0.9rem",
              width: "100%",
              minWidth: "400px",
            }}
          >
            <thead
              style={{
                position: "sticky",
                top: 0,
                background: "#f8f9fa",
                zIndex: 2,
              }}
            >
              <tr>
                <th>Tipo unidad</th>
                <th>Robos en el periodo</th>
                <th>Recuperados</th>
                <th>Boletinados</th>
                <th>Porcentaje de recuperación</th>
              </tr>
            </thead>
            <tbody>
              {dataFiltrada.map((x) => (
                <tr key={x.tipo_unidad}>
                  <td className="text-center">{x.tipo_unidad}</td>
                  <td className="text-center">{x.robos}</td>
                  <td className="text-center">{x.recuperados}</td>
                  <td className="text-center">{x.boletinados}</td>
                  <td className="text-center">
                    {x.recuperadas_porcentaje.toFixed(2)} %
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Carousel.Item>
    </Carousel>
  );
};

export const GraficaEventosRiesgo = ({ conteo = [] }) => {
  // Calculamos los totales por tipo de evento
  const data = [
    {
      name: "Robo con violencia",
      value: conteo.count?.reduce(
        (acc, item) => acc + (item.conteo_eventos?.con_violencia || 0),
        0
      ),
      color: "#FF0000",
    },
    {
      name: "Robo sin violencia",
      value: conteo.count?.reduce(
        (acc, item) => acc + (item.conteo_eventos?.sin_violencia || 0),
        0
      ),
      color: "#FF8C00",
    },
    {
      name: "Evento de riesgo",
      value: conteo.count?.reduce(
        (acc, item) => acc + (item.conteo_eventos?.otros_eventos || 0),
        0
      ),
      color: "#FFDB00",
    },
  ];

  // Filtramos solo los que tienen datos > 0
  const dataFiltrada = data.filter((d) => d.value > 0);

  // Si no hay datos, mostramos un mensaje (o puedes ocultar completamente)
  if (dataFiltrada.length === 0) {
    return (
      <Paper
        sx={{
          height: 300,
          borderRadius: "16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#888",
          fontStyle: "italic",
        }}
      >
        No hay datos disponibles para mostrar
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        height: 300,
        borderRadius: "16px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <Box sx={{ flex: 1 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={dataFiltrada}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={90}
              paddingAngle={3}
              dataKey="value"
              nameKey="name"
              labelLine={false}
              label={renderCustomizedLabel}
            >
              {dataFiltrada.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>

            <Tooltip
              formatter={(value, name) => [`${value}`, name]}
              contentStyle={{
                backgroundColor: "#fff",
                borderRadius: "8px",
                border: "1px solid #ccc",
              }}
            />
            <Legend
              verticalAlign="bottom"
              height={30}
              wrapperStyle={{
                fontSize: "0.8rem",
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
};

export const GraficaEventMonth = ({ data = [] }) => {
  // Evita error si algún mes no tiene valor válido
  const chartData = data.map((item) => ({
    ...item,
    etiqueta: `${mesesCatalogo[item.mes - 1]?.nombre.substring(
      0,
      3
    )}-${item.anio.toString().slice(-2)}`,
  }));

  return (
    <div style={{ width: "100%", height: "350px" }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          margin={{ top: 30, right: 30, left: 0, bottom: 30 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis dataKey="etiqueta" tick={{ fontSize: 12 }} />
          <YAxis />
          <Tooltip
            formatter={(value) => [`${value} eventos`, "Total"]}
            labelStyle={{ fontWeight: "bold" }}
          />
          <Bar dataKey="total_eventos" fill="#007BFF" radius={[6, 6, 0, 0]}>
            <LabelList
              dataKey="total_eventos"
              position="top"
              style={{
                fill: "#111",
                fontWeight: "bold",
                fontSize: 12,
              }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export const DataClient = ({ data = [] }) => {
  const agrupadoPorCliente = data.reduce((acc, curr) => {
    const { cliente, evento_seguridad = "", ultimo_estatus } = curr;

    // Normalizar el evento

    const tipo_robo = evento_seguridad;

    // Inicializar cliente si no existe
    if (!acc[cliente]) {
      acc[cliente] = {};
    }

    // Inicializar tipo de robo dentro del cliente si no existe
    if (!acc[cliente][tipo_robo]) {
      acc[cliente][tipo_robo] = {
        total: 0,
        recuperadas: 0,
        boletinas: 0,
      };
    }

    // Sumar conteo de robos
    acc[cliente][tipo_robo].total += 1;
    // acc[cliente][tipo_robo].boletinas += boletinas;
    console.log(ultimo_estatus);
    // Contar recuperadas
    if (
      ultimo_estatus ===
      "Vehículo Recuperado y Puesto a Disposición de Autoridad Competente"
    ) {
      acc[cliente][tipo_robo].recuperadas += 1;
    }

    if (ultimo_estatus === "Unidad no recuperada. Boletinada") {
      acc[cliente][tipo_robo].boletinas += 1;
    }

    return acc;
  }, {});

  return (
    <Carousel interval={10000} fade>
      {Object.entries(agrupadoPorCliente).map(([cliente, datos], index) => (
        <Carousel.Item key={index}>
          <div
            style={{
              border: "1px solid #ddd",
              borderRadius: "10px",
              padding: "10px",
              backgroundColor: "white",
              boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
              maxHeight: "350px",
              overflowY: "auto",
            }}
          >
            <Table
              hover
              bordered
              className="table-sm table-responsive"
              style={{ fontSize: "0.85rem" }}
            >
              <thead
                style={{
                  backgroundColor: "#2f5ea3",
                  color: "white",
                  textAlign: "center",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                }}
              >
                <tr>
                  <th style={{ width: "35%" }}>Tipo de Evento</th>
                  <th>Eventos en el período</th>
                  <th>Recuperadas</th>
                  <th>Boletinadas</th>
                  <th>% Recuperación</th>
                </tr>
              </thead>
              <tbody style={{ textAlign: "center" }}>
                {Object.entries(datos).map(([tipo, info], idx) => {
                  const porcentaje =
                    info.total > 0
                      ? ((info.recuperadas / info.total) * 100).toFixed(0)
                      : 0;
                  return (
                    <tr key={idx}>
                      <td>{tipo}</td>
                      <td>{info.total}</td>
                      <td>{info.recuperadas}</td>
                      <td>{info.boletinas}</td>
                      <td>{porcentaje}%</td>
                    </tr>
                  );
                })}
              </tbody>
              <caption
                style={{
                  captionSide: "bottom",
                  textAlign: "right",
                  paddingRight: "0.5rem",
                  fontStyle: "italic",
                }}
              >
                {cliente === "N/A" ? "Cliente no registrado" : cliente}
              </caption>
            </Table>
          </div>
        </Carousel.Item>
      ))}
    </Carousel>
  );
};
